import { useCallback, useState } from 'react';
import api, { type SupportAdminCollection } from '@/lib/api';

/**
 * Drives the "No reply needed" checkbox in a submission's edit dialog.
 *
 * The four non-threaded admin pages (suggestions, bug reports, builder problem
 * reports, Fias AI reports) each save status and admin notes through their own
 * update endpoint, and the waiver through a separate one. That is two writes
 * per save, and the ORDER matters: the waiver goes first, so a failure there
 * leaves nothing committed behind the error message. Saving the status first
 * and the waiver second leaves a half-applied edit behind a failed dialog.
 *
 * `save()` is a no-op when the checkbox never moved, so an ordinary
 * status-only edit still costs exactly one request.
 */
export function useReplyWaiver(collection: SupportAdminCollection) {
  const [waived, setWaived] = useState(false);
  const [initial, setInitial] = useState(false);

  /** Seed from the record the dialog just opened. */
  const reset = useCallback((current: boolean | undefined) => {
    setWaived(current ?? false);
    setInitial(current ?? false);
  }, []);

  /** Persist, if and only if the admin actually moved the checkbox. */
  const save = useCallback(
    async (id: string) => {
      if (waived === initial) return;
      await api.setSupportReplyWaivedAdmin(collection, id, waived);
    },
    [collection, waived, initial],
  );

  return { waived, setWaived, reset, save };
}
